import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { useTranslation } from "react-i18next";
import { BarChart3, TrendingUp, Wallet, PieChart, ArrowUpRight, ArrowDownRight, Activity, Calendar } from "lucide-react";
import walletService from "../services/walletService";
import PortfolioChart from "../components/PortfolioChart";

export default function Stats() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [walletsData, statsData] = await Promise.all([
        walletService.getWallets(),
        walletService.getWalletStats()
      ]);
      setWallets(walletsData);
      setStats(statsData);
    } catch (error) {
      console.error("Error loading stats data:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-slate-950 text-white font-inter">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <main className="flex-1 p-6 md:p-10 overflow-y-auto custom-scrollbar relative">
          {/* Background Aura */}
          <div className="absolute top-0 left-0 w-full h-full bg-indigo-500/5 blur-[150px] -z-10 pointer-events-none"></div>
          
          <div className="max-w-7xl mx-auto space-y-10 animate-fade-in">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-b border-white/5 pb-10">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-indigo-500/10 rounded-lg border border-indigo-500/20">
                    <BarChart3 className="w-5 h-5 text-indigo-400" />
                  </div>
                  <span className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.3em]">Advanced Analytics</span>
                </div>
                <h1 className="text-4xl font-black text-white font-outfit tracking-tight leading-none">
                  Performance <span className="text-slate-500">Statistics</span>
                </h1>
              </div>
              
              <div className="flex items-center gap-4 bg-slate-900/40 p-1.5 rounded-2xl border border-white/5">
                {['24H', '7D', '30D', 'ALL'].map((range) => (
                  <button 
                    key={range}
                    className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${range === '30D' ? 'bg-indigo-500 text-white' : 'text-slate-500 hover:text-slate-300'}`}
                  >
                    {range}
                  </button>
                ))}
              </div>
            </div>

            {/* Top Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { label: 'Total Equity', value: `$${(stats?.total_balance || 0).toLocaleString()}`, icon: <Wallet />, color: 'text-indigo-400', bg: 'bg-indigo-500/10' },
                { label: 'Monthly Profit', value: `$${(stats?.monthly_profit || 0).toLocaleString()}`, icon: <TrendingUp />, color: 'text-emerald-400', bg: 'bg-emerald-500/10', trend: stats?.monthly_percent },
                { label: 'Total Assets', value: wallets.length, icon: <PieChart />, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
                { label: 'Activity Score', value: '98.2', icon: <Activity />, color: 'text-purple-400', bg: 'bg-purple-500/10' },
              ].map((m, i) => (
                <div key={i} className="glass-card p-6 border-white/5 group hover:border-indigo-500/20 transition-all">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 rounded-xl ${m.bg} ${m.color} border border-white/5 group-hover:scale-110 transition-transform`}>
                      {React.cloneElement(m.icon, { size: 20 })}
                    </div>
                    {m.trend && (
                      <div className={`px-2 py-1 rounded-lg bg-emerald-500/10 text-emerald-400 text-[10px] font-black flex items-center gap-1`}>
                        <ArrowUpRight size={12} />
                        {m.trend}
                      </div>
                    )}
                  </div>
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-1">{m.label}</p>
                  <h3 className="text-2xl font-black text-white font-outfit tracking-tight">{m.value}</h3>
                </div>
              ))}
            </div>

            {/* Main Visual Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2 space-y-10">
                <div className="glass-card p-10 border-white/5 relative overflow-hidden group">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 blur-[100px] -mr-32 -mt-32"></div>
                  <div className="flex items-center justify-between mb-10">
                    <div>
                      <h3 className="text-lg font-black text-white font-outfit uppercase tracking-widest mb-1">Growth Overview</h3>
                      <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Portfolio valuation over time</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                            <span className="text-[9px] font-black text-slate-400 uppercase">USDT Value</span>
                        </div>
                    </div>
                  </div>
                  {/* Simulated Chart Placeholder for now - Could use Recharts later */}
                  <div className="h-[300px] w-full flex items-end gap-2 px-2 pb-2 border-b border-l border-white/5">
                    {[40, 60, 45, 70, 85, 65, 90, 100, 80, 95, 110, 120].map((h, i) => (
                      <div 
                        key={i} 
                        className="flex-1 bg-gradient-to-t from-indigo-500/20 to-indigo-500/60 rounded-t-lg group/bar relative"
                        style={{ height: `${h}%` }}
                      >
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-indigo-500 text-white text-[8px] font-black px-1.5 py-0.5 rounded opacity-0 group-hover/bar:opacity-100 transition-opacity">
                          ${(30000 + h * 50).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between mt-4 px-2">
                    {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map(m => (
                      <span key={m} className="text-[9px] font-black text-slate-600 uppercase tracking-tighter">{m}</span>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="glass-card p-8 border-white/5">
                    <h4 className="text-xs font-black text-white uppercase tracking-widest mb-6">Trading Efficiency</h4>
                    <div className="space-y-6">
                      {[
                        { label: 'Win Rate', value: '74.2%', color: 'bg-emerald-500' },
                        { label: 'Profit Factor', value: '2.84', color: 'bg-indigo-500' },
                        { label: 'Avg Trade', value: '$124.50', color: 'bg-cyan-500' },
                      ].map((item, i) => (
                        <div key={i}>
                          <div className="flex justify-between text-[10px] font-black uppercase mb-2">
                            <span className="text-slate-500">{item.label}</span>
                            <span className="text-white">{item.value}</span>
                          </div>
                          <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                            <div className={`h-full ${item.color} rounded-full`} style={{ width: i === 0 ? '74%' : i === 1 ? '60%' : '45%' }}></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="glass-card p-8 border-white/5 flex flex-col justify-center items-center text-center">
                    <div className="w-16 h-16 rounded-3xl bg-indigo-500/10 flex items-center justify-center mb-4 border border-indigo-500/20">
                      <Calendar className="text-indigo-400" size={28} />
                    </div>
                    <h4 className="text-xs font-black text-white uppercase tracking-widest mb-1">Time Analysis</h4>
                    <p className="text-[10px] text-slate-500 font-bold uppercase mb-4 tracking-tighter">Your best trading hours</p>
                    <div className="flex gap-1.5">
                      {[3, 5, 8, 4, 6, 9, 7].map((h, i) => (
                        <div key={i} className="w-4 bg-white/5 rounded-t-sm relative h-12 flex items-end">
                          <div className="w-full bg-indigo-500/40 rounded-t-sm" style={{ height: `${h * 10}%` }}></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-10">
                <div className="glass-card p-8 border-white/5 bg-slate-900/30 backdrop-blur-xl">
                  <PortfolioChart wallets={wallets} totalBalance={stats?.total_balance || 0} />
                </div>

                <div className="glass-card p-8 border-emerald-500/10 bg-emerald-500/5">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <TrendingUp className="text-emerald-400" size={20} />
                    </div>
                    <div>
                      <h4 className="text-[10px] font-black text-emerald-500/50 uppercase tracking-widest">ROI Report</h4>
                      <p className="text-xs font-black text-white uppercase tracking-widest font-outfit">Performance OK</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between items-end border-b border-white/5 pb-4">
                      <span className="text-[10px] font-black text-slate-500 uppercase">Daily Avg</span>
                      <span className="text-lg font-black text-white font-outfit">+$42.15</span>
                    </div>
                    <div className="flex justify-between items-end">
                      <span className="text-[10px] font-black text-slate-500 uppercase">Projection</span>
                      <span className="text-lg font-black text-emerald-400 font-outfit">+$1,264.50</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
